import React, { useEffect, useState, useCallback, useRef } from "react";
import API from "../../api/api";
import "../../style/payment.css";

export default function Payments() {
  const [data, setData] = useState([]);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const cancelRef = useRef(null);
  const debounceRef = useRef(null);

  // ✅ Filters
  const [filters, setFilters] = useState({
    status: "",
    plan: "",
    search: "",
    from: "",
    to: "",
    sort: "latest"
  });

  // 🔥 Build clean query
  const buildQuery = () => {
    const q = { page };

   if (filters.status) q.status = filters.status.toLowerCase();
    if (filters.plan) q.plan = filters.plan;
    if (filters.search) q.search = filters.search.trim();

    if (filters.from) {
      q.from = new Date(filters.from).toISOString();
    }

    if (filters.to) {
      const end = new Date(filters.to);
      end.setHours(23, 59, 59, 999);
      q.to = end.toISOString();
    }

    if (filters.sort) q.sort = filters.sort;

    return new URLSearchParams(q);
  };

  // 🔥 Fetch
  const fetchPayments = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      if (cancelRef.current) cancelRef.current.abort();

      const controller = new AbortController();
      cancelRef.current = controller;

      const query = buildQuery();

      const res = await API.get(`/admin/payments?${query}`, {
        signal: controller.signal
      });

      const response = res?.data || {};

      setData(response.data || []);
      setPagination(response.pagination || {});

    } catch (err) {
      if (err.name !== "AbortError") {
        console.error(err);
        setError("Failed to load payments");
      }
    } finally {
      setLoading(false);
    }
  }, [page, filters]);

  // 🔥 Debounce
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(fetchPayments, 400);

    return () => clearTimeout(debounceRef.current);
  }, [fetchPayments]);

  // 🔥 Handle filters
  const handleChange = (e) => {
    const { name, value } = e.target;
    setPage(1);
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  // 🔥 Reset filters
  const resetFilters = () => {
    setPage(1);
    setFilters({
      status: "",
      plan: "",
      search: "",
      from: "",
      to: "",
      sort: "latest"
    });
  };

  // 🔥 Pagination
  const goPrev = () => setPage(p => Math.max(1, p - 1));
  const goNext = () =>
    setPage(p => (pagination.pages ? Math.min(p + 1, pagination.pages) : p));

  return (
    <div className="admin-page payments-page">

      <h2 className="page-title">Payments</h2>

      {/* ERROR */}
      {error && <div className="error">{error}</div>}

      {/* FILTERS */}
      <div className="filters">
        <input
          type="text"
          name="search"
          placeholder="Search user..."
          value={filters.search}
          onChange={handleChange}
        />

        <select name="status" value={filters.status} onChange={handleChange}>
          <option value="">All Status</option>
          <option value="success">Success</option>
          <option value="failed">Failed</option>
          <option value="pending">Pending</option>
        </select>

        <select name="plan" value={filters.plan} onChange={handleChange}>
          <option value="">All Plans</option>
          <option value="basic">Basic</option>
          <option value="pro">Pro</option>
        </select>

        <input type="date" name="from" value={filters.from} onChange={handleChange} />
        <input type="date" name="to" value={filters.to} onChange={handleChange} />

        <select name="sort" value={filters.sort} onChange={handleChange}>
          <option value="latest">Latest</option>
          <option value="amount">Amount</option>
        </select>

        <button onClick={resetFilters} className="btn-reset">
          Reset
        </button>
      </div>

      {/* TABLE */}
      {loading ? (
        <div className="loader">Loading...</div>
      ) : data.length === 0 ? (
        <div className="empty">No payments found</div>
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th>User</th>
              <th>Plan</th>
              <th>Amount</th>
              <th>Status</th>
              <th>Date</th>
            </tr>
          </thead>

          <tbody>
            {data.map(p => (
              <tr key={p._id}>
                <td>{p?.userId?.name || "N/A"}</td>
                <td>{p?.planName}</td>
                <td>₹{p?.amountPaid}</td>
                <td className={`status ${p?.paymentStatus}`}>
                  {p?.paymentStatus}
                </td>
                <td>
                  {p?.createdAt
                    ? new Date(p.createdAt).toLocaleString()
                    : "-"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* PAGINATION */}
      <div className="pagination">
        <button onClick={goPrev} disabled={page === 1}>
          Prev
        </button>

        <span>{page} / {pagination.pages || 1}</span>

        <button
          onClick={goNext}
          disabled={!pagination.pages || page === pagination.pages}
        >
          Next
        </button>
      </div>

    </div>
  );
}